import React, { useEffect, useCallback, useMemo } from 'react';

import { useEnvironment } from '@wix/yoshi-flow-editor';

import { useAppDispatch, useAppSelector, useParametricSelector } from '@/store';
import {
  selectOneChatSettings,
  getEditorMockMessages,
  subscribeForNewMessages,
  selectMessages,
  getConversationId,
  DummyMessageType,
  getConversationsQuotaThrottled,
  expandWidget,
  minimizeWidget,
  messagesSlice,
  flagsSlice,
  getOneChatSettings,
  selectOneChatComposed,
  oneChatSettingsSlice,
} from '@/features';
import {
  usePresenceClient,
  useResizeWidgetWindow,
  useForcedExpandInEditor,
  useSessionNavigation,
  useForcedExpandInViewer,
  useReportMissingInstanceToken,
  useReportNewVisitor,
  usePanoramaTransaction,
} from '@/hooks';
import { PanoramaTransactions } from '@/panoramaTransactions';
import { isStudioSite } from '@/utils';
import { CurrentStatus } from '@/features/oneChatSettings/model';

import {
  BubbleState,
  ChatVisibility,
  settingsEvents,
} from 'AiAssistantWidget/settingsEvents';

import {
  useOnEditorSettingsEvent,
  useOnEditorSettingsReset,
  useRootProps,
} from '../../contexts';
import { MinimizedChat } from '../MinimizedChat';
import { OneChat } from '../Chat';

import { shouldShowMinimizedOneChatWidget } from './shouldShowMinimizedOneChatWidget';

export const OneChatChatWrapper: React.FC = () => {
  const dispatch = useAppDispatch();
  const { isEditor, isMobile, isEditorX: isStudioEditor } = useEnvironment();
  const { page } = useRootProps();
  useSessionNavigation();

  const shouldGetConversationId = useAppSelector(
    selectMessages.shouldLoadConversationId,
  );
  const conversationId = useAppSelector(selectMessages.conversationId);
  const neverLoadedSettings = useAppSelector(selectOneChatSettings.neverLoaded);
  const isSettingsLoaded = useAppSelector(selectOneChatSettings.isLoaded);
  const shouldShowWidget = useParametricSelector(
    () => selectOneChatComposed.shouldShowWidget,
    { isMobile, pageId: page?.id },
  );
  const { setRef, isOpened } = useResizeWidgetWindow();

  const shouldShowMinimizedWidget = useMemo(
    () =>
      shouldShowMinimizedOneChatWidget({
        isMobile,
        isOpened,
        isStudioSite: isStudioSite(),
        isStudioEditor,
      }),
    [isOpened, isMobile, isStudioEditor],
  );

  usePanoramaTransaction(
    isEditor
      ? PanoramaTransactions.WIDGET_LOAD_EDITOR
      : PanoramaTransactions.WIDGET_LOAD_VIEWER,
    isSettingsLoaded,
  );

  usePresenceClient();
  useReportMissingInstanceToken();
  useReportNewVisitor();

  useOnEditorSettingsEvent(
    settingsEvents.visual,
    useCallback(
      (state) => {
        if (!isEditor) {
          return;
        }
        switch (state) {
          case ChatVisibility.Visible:
            dispatch(expandWidget());
            break;
          case ChatVisibility.Minimized:
            dispatch(minimizeWidget());
            break;
        }
      },
      [dispatch, isEditor],
    ),
  );

  useOnEditorSettingsEvent(
    settingsEvents.status,
    useCallback(
      (status) => {
        if (!isEditor) {
          return;
        }
        switch (status) {
          case CurrentStatus.AI_ASSISTANT_ONLINE:
            dispatch(oneChatSettingsSlice.actions.setCurrentStatus(status));
            break;
          case CurrentStatus.MANUAL_ONLINE:
            dispatch(oneChatSettingsSlice.actions.setCurrentStatus(status));
            break;
          case CurrentStatus.OFFLINE:
            // NOTE: This is not used at the moment. Added just in case for future use.
            dispatch(oneChatSettingsSlice.actions.setCurrentStatus(status));
            break;
        }
      },
      [dispatch, isEditor],
    ),
  );

  useForcedExpandInViewer();

  useOnEditorSettingsReset(() => {
    dispatch(minimizeWidget());
    dispatch(messagesSlice.actions.resetMessages({ messages: [] }));
    dispatch(messagesSlice.actions.setUnread(false));
  }, []);

  useForcedExpandInEditor();

  useOnEditorSettingsEvent(
    settingsEvents.openMessages,
    useCallback(() => {
      if (!isEditor) {
        return;
      }

      dispatch(expandWidget());
      dispatch(getEditorMockMessages());
    }, [dispatch, isEditor]),
  );
  useOnEditorSettingsEvent(
    settingsEvents.openVisitorMessages,
    useCallback(() => {
      if (!isEditor) {
        return;
      }
      dispatch(expandWidget());
      dispatch(getEditorMockMessages(DummyMessageType.Visitor));
    }, [dispatch, isEditor]),
  );
  useOnEditorSettingsEvent(
    settingsEvents.openContactForm,
    useCallback(() => {
      if (!isEditor) {
        return;
      }
      dispatch(expandWidget());
      dispatch(flagsSlice.actions.setIsContact(false));
      dispatch(
        oneChatSettingsSlice.actions.setContactForm({
          includeEmail: true,
          includeName: true,
        }),
      );
      dispatch(getEditorMockMessages(DummyMessageType.ContactForm));
    }, [dispatch, isEditor]),
  );
  useOnEditorSettingsEvent(
    settingsEvents.openAssistantMessages,
    useCallback(() => {
      if (!isEditor) {
        return;
      }
      dispatch(expandWidget());
      dispatch(getEditorMockMessages(DummyMessageType.Assistant));
    }, [dispatch, isEditor]),
  );
  useOnEditorSettingsEvent(
    settingsEvents.openCardMessages,
    useCallback(() => {
      if (!isEditor) {
        return;
      }
      dispatch(expandWidget());
      dispatch(getEditorMockMessages(DummyMessageType.Card));
    }, [dispatch, isEditor]),
  );
  useOnEditorSettingsEvent(
    settingsEvents.bubble,
    useCallback(
      (state) => {
        if (!isEditor) {
          return;
        }
        switch (state) {
          case BubbleState.Visible:
            dispatch(minimizeWidget());
            dispatch(getEditorMockMessages(DummyMessageType.Bubble));
            dispatch(messagesSlice.actions.setUnread(true));
            break;
          case BubbleState.Hidden:
            dispatch(
              messagesSlice.actions.resetMessages({
                messages: [],
                pagingMetadata: undefined,
              }),
            );
            dispatch(messagesSlice.actions.setUnread(false));
            break;
        }
      },
      [dispatch, isEditor],
    ),
  );
  useEffect(() => {
    if (isEditor) {
      dispatch(getEditorMockMessages());
    }
  }, [dispatch, isEditor]);

  useEffect(() => {
    let abort = false;

    // Settings are needed in Editor as well for displaying minimized widget defferences
    // https://github.com/wix-private/wix-ai-assistant-bot/issues/758
    if (neverLoadedSettings) {
      dispatch(getOneChatSettings());
    }

    if (isEditor) {
      return;
    }

    if (shouldGetConversationId) {
      dispatch(getConversationId());
      return; // no need to subscribe for new messages if we don't have conversationId
    }

    let unsubscribeFromNewMessages: undefined | (() => void);
    if (conversationId) {
      dispatch(subscribeForNewMessages())
        .unwrap()
        .then((unsubscribe) => {
          if (abort) {
            unsubscribe?.();
            return;
          }
          unsubscribeFromNewMessages = unsubscribe;
        });
    }

    return () => {
      abort = true;
      unsubscribeFromNewMessages?.();
    };
  }, [
    isEditor,
    neverLoadedSettings,
    shouldGetConversationId,
    conversationId,

    dispatch,
  ]);

  useEffect(() => {
    dispatch(getConversationsQuotaThrottled());
  }, [dispatch]);

  // TODO: this if should be covered by tests in Editor and Live site env
  if (!isEditor && !shouldShowWidget) {
    console.warn(
      `Chat is not shown due: "shouldShowWidget: ${shouldShowWidget}" OR "isEditor: ${isEditor}" being false`,
    );
    return null;
  }
  return (
    <>
      {/* Read note at EditorWrapper file */}
      {/* <EditorWrapper> } */}
      {isOpened && <OneChat />}
      {shouldShowMinimizedWidget && <MinimizedChat setRef={setRef} />}
      {/* </EditorWrapper> */}
    </>
  );
};
